import { AuthService } from '../service/auth.service';
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { httpErrorMessage } from '../utils/http-error-message';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshDone$ = new BehaviorSubject<boolean | null>(null);

  constructor(private authenticationService: AuthService) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Only intercept API requests (skip static assets like i18n files)
    if (!request.url.includes('/api/')) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((err) => {
        if (err.status === 401 && !request.url.includes('/api/auth/')) {
          return this.handle401(request, next);
        }

        return throwError(() => httpErrorMessage(err));
      })
    );
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshDone$.next(null);
      return this.authenticationService.refreshToken().pipe(
        switchMap((res) => {
          this.isRefreshing = false;
          this.refreshDone$.next(true);
          const token = res.accessToken;
          const cloned = request.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
          });
          return next.handle(cloned);
        }),
        catchError((refreshErr) => {
          this.isRefreshing = false;
          this.refreshDone$.next(false);
          this.authenticationService.logout();
          location.reload();
          return throwError(() => refreshErr);
        })
      );
    }

    return this.refreshDone$.pipe(
      filter((v) => v !== null),
      take(1),
      switchMap((ok) => {
        if (!ok) {
          return throwError(() => 'Session expired');
        }
        const token = this.authenticationService.currentUserValue?.token;
        const cloned = request.clone({
          setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return next.handle(cloned);
      })
    );
  }
}
